/**
 * Zod schemas for /api/search/tags endpoint
 * Tag-based search for listings using ElasticSearch
 */

import { z } from 'zod';
import { parseListingSort } from '../../../utils/listing-sort';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const TagSearchRequestSchema = z.object({
  tag: z.string().optional(),
  tags: z.string().optional(),
  q: z.string().optional(),
  categoryId: z.string().optional(),
  categoryGroupId: z.string().optional(),
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  itemCondition: z.string().optional(),
  shippable: z.string().optional(),
  userId: z.string().optional(),
  dataSourceId: z.string().optional(),
  hasPhoto: z.string().optional(),
  page: z.string().optional().default('1'),
  limit: z.string().optional().default('20'),
  status: z.string().optional(),
  // A shopper-picked order (utils/listing-sort.ts); anything else reads as none.
  sortBy: z.string().optional().transform((val) => parseListingSort(val)),
});

export type TagSearchRequest = z.infer<typeof TagSearchRequestSchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

// Tag metadata schema
export const TagMetadataSchema = z.object({
  headings: z.array(z.string()),
  copy: z.array(z.string()),
  images: z.array(z.string()),
  count: z.number(),
  lastUsed: z.union([z.date(), z.string()]),
});

export type TagMetadata = z.infer<typeof TagMetadataSchema>;

// Photo schema for tag search listings.
// Sized variants mirror getListingPhotos() — the tag grid renders through
// listing-thumb-varied, which reaches for 640x640Url on tall cards.
export const TagSearchPhotoSchema = z.object({
  url: z.string(),
  thumbUrl: z.string().optional(),
  '160x160Url': z.string().optional(),
  '320x320Url': z.string().optional(),
  '640x640Url': z.string().optional(),
  '1280x1280Url': z.string().optional(),
});

export type TagSearchPhoto = z.infer<typeof TagSearchPhotoSchema>;

// User schema for tag search listings
export const TagSearchUserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  trustLevel: z.number(),
});

export type TagSearchUser = z.infer<typeof TagSearchUserSchema>;

// Tag search listing schema
export const TagSearchListingSchema = z.object({
  id: z.union([z.string(), z.number()]),
  title: z.string(),
  text: z.string().nullish(),
  price: z.number(),
  originalPrice: z.number().optional(),
  cost: z.number(),
  categoryId: z.number(),
  categoryGroupId: z.number(),
  itemCondition: z.number().nullish(),
  status: z.number(),
  shippable: z.union([z.number(), z.boolean()]),
  locationText: z.string().nullish(),
  createdDate: z.string().nullish(),
  tags: z.array(z.string()).optional(),
  height: z.number().nullish(),
  width: z.number().nullish(),
  depth: z.number().nullish(),
  weight: z.number().nullish(),
  _tagGroup: z.string().optional(),
  user: TagSearchUserSchema,
  photos: z.array(TagSearchPhotoSchema),
  distance: z.number(),
});

export type TagSearchListing = z.infer<typeof TagSearchListingSchema>;

// Success response schema
export const TagSearchSuccessResponseSchema = z.object({
  success: z.literal(true),
  listings: z.array(TagSearchListingSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
  tagMetadata: z.record(z.string(), TagMetadataSchema).optional(),
  groupCounts: z.record(z.string(), z.number()).optional(),
});

export type TagSearchSuccessResponse = z.infer<typeof TagSearchSuccessResponseSchema>;

// Error response schema
export const TagSearchErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type TagSearchErrorResponse = z.infer<typeof TagSearchErrorResponseSchema>;

// Combined response schema using union
export const TagSearchResponseSchema = z.union([
  TagSearchSuccessResponseSchema,
  TagSearchErrorResponseSchema,
]);

export type TagSearchResponse = z.infer<typeof TagSearchResponseSchema>;
